import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { MilestoneTask } from "../../features/taskReports/MilestoneTask";
import TimeFormatter from "../../features/extensions/TimeFormatter";

interface TimeEntriesTableProps {
  task: MilestoneTask;
}

const TaskReportTimeEntriesTable = ({
  task,
}: TimeEntriesTableProps) => {
  const timeFormatter = new TimeFormatter();

  const shouldShowTypeOfWork = useMemo(() => {
    if (!task.timeEntries || task.timeEntries.length === 0) return true;
    return task.timeEntries.some(
      (entry) => entry.typeOfWork && entry.typeOfWork !== "N/A"
    );
  }, [task.timeEntries]);

  const shouldShowPercentageComplete = useMemo(() => {
    if (!task.timeEntries || task.timeEntries.length === 0) return true;
    return task.timeEntries.some(
      (entry) =>
        entry.percentageComplete !== undefined &&
        entry.percentageComplete !== null &&
        entry.percentageComplete > 0
    );
  }, [task.timeEntries]);

  return (
    <Box
      sx={{
        margin: 1,
        marginBottom: 0,
        borderRadius: 1,
      }}
    >
      <Typography variant="h6" gutterBottom component="div">
        Time Entries of Task Number {task.gitLabTaskNumber}
      </Typography>
      <Table size="small" aria-label="time entries">
        <TableHead>
          <TableRow>
            <TableCell>Developer Name</TableCell>
            <TableCell>Description</TableCell>
            {shouldShowTypeOfWork && <TableCell>Type of Work</TableCell>}
            <TableCell>Date</TableCell>
            <TableCell align="right" sx={{ width: "10%" }}>
              Time on task
            </TableCell>
            {shouldShowPercentageComplete && (
              <TableCell sx={{ width: "10%" }} align="right">
                % Complete
              </TableCell>
            )}
            <TableCell align="right">Cumulative time</TableCell>
            <TableCell align="right" sx={{ width: "10%" }}>
              Budgetary Performance
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...task.timeEntries]
            .sort(
              (a, b) =>
                new Date(b.dateEnd).getTime() - new Date(a.dateEnd).getTime()
            )
            .map((entry, index) => {
              const cumulativeTimeSpent = task.timeEntries
                .filter((e) => new Date(e.dateEnd) <= new Date(entry.dateEnd))
                .reduce((sum, e) => sum + (e.timeSpentInHours || 0), 0);

              const budgetaryPerformance =
                typeof task.timeEstimate === "number" &&
                task.timeEstimate > 0 &&
                entry.percentageComplete > 0 &&
                cumulativeTimeSpent > 0 &&
                cumulativeTimeSpent
                  ? cumulativeTimeSpent /
                    ((task.timeEstimate * entry.percentageComplete) / 100)
                  : 0;

              return (
                <TableRow
                  key={`${entry.id} + ${index}`}
                  sx={{
                    "&:last-child td, &:last-child th": {
                      border: 0,
                    },
                  }}
                >
                  <TableCell component="th" scope="row">
                    {entry.developerName}
                  </TableCell>
                  <TableCell>{entry.description || "N/A"}</TableCell>
                  {shouldShowTypeOfWork && (
                    <TableCell>{entry.typeOfWork || "N/A"}</TableCell>
                  )}
                  <TableCell>
                    {new Date(entry.dateEnd).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    {timeFormatter.toHoursAndMinutes(entry.timeSpentInHours)}
                  </TableCell>
                  {shouldShowPercentageComplete && (
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "1px solid",
                          borderColor:
                            entry.percentageComplete === 0
                              ? "grey"
                              : entry.percentageComplete === 100
                              ? "success.main"
                              : entry.percentageComplete >= 75
                              ? "info.main"
                              : entry.percentageComplete >= 50
                              ? "warning.main"
                              : "primary.main",
                          borderRadius: "16px",
                          padding: "0 8px",
                          height: "24px",
                          lineHeight: "20px",
                          color:
                            entry.percentageComplete === 0
                              ? "grey"
                              : entry.percentageComplete === 100
                              ? "success.main"
                              : entry.percentageComplete >= 75
                              ? "info.main"
                              : entry.percentageComplete >= 50
                              ? "warning.main"
                              : "primary.main",
                        }}
                      >
                        {`${entry.percentageComplete}%`}
                      </Box>
                    </TableCell>
                  )}
                  <TableCell align="right">
                    {timeFormatter.toHoursAndMinutes(cumulativeTimeSpent)}
                  </TableCell>
                  <TableCell align="right">
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px solid",
                        borderColor:
                          budgetaryPerformance === 0
                            ? "grey"
                            : budgetaryPerformance <= 1
                            ? "success.main"
                            : budgetaryPerformance <= 1.25
                            ? "warning.main"
                            : "error.main",
                        borderRadius: "16px",
                        padding: "0 8px",
                        height: "24px",
                        lineHeight: "20px",
                        color:
                          budgetaryPerformance === 0
                            ? "grey"
                            : budgetaryPerformance <= 1
                            ? "success.main"
                            : budgetaryPerformance <= 1.25
                            ? "warning.main"
                            : "error.main",
                      }}
                    >
                      {budgetaryPerformance.toFixed(2)}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
    </Box>
  );
};

export default TaskReportTimeEntriesTable;
